<template>
  <div class="contractDetailsForm">
    <u-error-handling
      v-if="errorObject"
      :error-object="errorObject"
    />
    <u-loader v-if="isLoading" />
    <div v-else-if="contract">
      <button
        v-if="
          !editMode &&
            ($IsUserInRole('VertragsmanagementVerwaltung') ||
            $IsUserInRole('VertragsmanagementVetragsDetails'))
        "
        v-b-tooltip.hover
        class="btn btn-primary float-right"
        placement="bottom"
        title="Vertragsdetails bearbeiten"
        type="button"
        @click="editMode = !editMode"
      >
        <i class="fas fa-edit" />
      </button>
      <h3
        v-if="contract"
        class="card-title font-weight-bold"
      >
        Vertragsdetails - {{ contract.contractNumber }}
        {{ latestVersionShortName ? " " + latestVersionShortName : "" }}
      </h3>

      <form v-if="contract">
        <div class="form-group">
          <b-container class="pb-0 pt-0">
            <b-row>
              <label class="pl-0 col-3 defaultLabel">Tags</label>
              <div
                v-if="!editMode"
                class="col-9 pl-0"
              >
                <div v-if="contract.tagList && contract.tagList.length">
                  <b-row>
                    <v-chip
                      v-for="(tag, i) in contract.tagList"
                      :key="i"
                    >
                      {{ tag }}
                    </v-chip>
                  </b-row>
                </div>

                <div v-else>
                  -
                </div>
              </div>
              <div
                v-else
                class="col-9 pl-0"
              >
                <v-flex xs12>
                  <v-combobox
                    v-if="$IsUserInRole('KonfigurationContentmanager')"
                    v-model="contract.tagList"
                    :disabled="!editMode"
                    :items="itemsTags"
                    chips
                    clearable
                    deletable-chips
                    label="Tags"
                    multiple
                    @change="tagsChanged()"
                  />
                  <v-select
                    v-else
                    v-model="contract.tagList"
                    :disabled="!editMode"
                    :items="itemsTags"
                    :menu-props="{ offsetY: true }"
                    chips
                    clearable
                    deletable-chips
                    label="Tags"
                    multiple
                  />
                </v-flex>
              </div>
            </b-row>
          </b-container>
        </div>

        <div class="form-group">
          <b-container class="pb-0 pt-0">
            <b-row>
              <label
                :for="contract.contractNumber"
                class="pl-0 col-3 defaultLabel"
              >Vertragsnummer</label>
              <div
                v-if="!editMode"
                class="col-9 pl-0"
              >
                {{ contract.contractNumber }}
              </div>
              <div
                v-else
                class="col-9 pl-0"
              >
                <v-text-field
                  v-model="contract.contractNumber"
                  label="Vertragsnummer"
                  :disabled="true"
                />
                <u-validation-checker
                  v-model="requiredProperties"
                  :is-empty="!contract.contractNumber"
                  :section="validationSection"
                  property-name="contractNumber"
                />
              </div>
            </b-row>
          </b-container>
        </div>

        <div class="form-group">
          <b-container class="pb-0 pt-0">
            <b-row>
              <label
                :for="contract.value"
                class="pl-0 col-3 defaultLabel"
              >Originalsprache</label>
              <div
                v-if="!editMode"
                class="col-9 pl-0"
              >
                {{
                  {
                    "de-CH": "Deutsch",
                    "fr-CH": "Französisch",
                    "it-CH": "Italienisch",
                  }[contract.contractLanguage]
                }}
              </div>
              <div
                v-else
                class="col-9 pl-0"
              >
                <v-select
                  v-model="contract.contractLanguage"
                  :disabled="
                    $IsUserInRole('VertragsmanagementVetragsDetails') &&
                      !$IsUserInRole('VertragsmanagementVerwaltung')
                  "
                  :items="contractLanguages"
                  item-text="label"
                  item-value="language"
                />
                <u-validation-checker
                  v-model="requiredProperties"
                  :is-empty="false"
                  :section="validationSection"
                  property-name="contractLanguage"
                />
              </div>
            </b-row>
          </b-container>
        </div>

        <div class="form-group">
          <b-container class="pb-0 pt-0">
            <b-row>
              <label
                :for="contract.value"
                class="pl-0 col-3 defaultLabel"
              >Vertragstyp</label>
              <div
                v-if="!editMode"
                class="col-9 pl-0"
              >
                {{ contractType }}
              </div>
              <div
                v-else
                class="col-9 pl-0"
              >
                <v-select
                  v-model="contract.contractTypeId"
                  :disabled="
                    $IsUserInRole('VertragsmanagementVetragsDetails') &&
                      !$IsUserInRole('VertragsmanagementVerwaltung')
                  "
                  :items="contractTypes"
                  item-text="name"
                  item-value="id"
                />
                <u-validation-checker
                  v-model="requiredProperties"
                  :is-empty="false"
                  :section="validationSection"
                  property-name="contractTypeId"
                />
              </div>
            </b-row>
          </b-container>
        </div>

        <!-- Ergänzungsverträge -->
        <div
          v-if="contract.contractTypeId !== 5"
          class="form-group"
        >
          <b-container class="pb-0 pt-0">
            <b-row>
              <label
                :for="contract.value"
                class="pl-0 col-3 defaultLabel"
              >Ergänzungsverträge</label>
              <div class="col-9 pl-0">
                <div v-if="!editMode">
                  <ul
                    v-if="
                      supplementaryContracts && supplementaryContracts.length
                    "
                    @click="$router.go()"
                  >
                    <li
                      v-for="(suppContract, index) in supplementaryContracts"
                      :key="index"
                    >
                      <router-link
                        :key="index"
                        :to="
                          '/contract-overview/' + suppContract.contractNumber
                        "
                      >
                        {{ suppContract.contractNumber }} -
                        {{ getContractName(suppContract.contractNumber) }}
                      </router-link>
                    </li>
                  </ul>
                  <div v-else>
                    -
                  </div>
                </div>
                <v-autocomplete
                  v-else
                  v-model="supplementaryContracts"
                  :filter="filterContracts"
                  :items="availableSupplementaryContracts"
                  item-value="contractNumber"
                  multiple
                  return-object
                  :disabled="!$IsUserInRole('VertragsmanagementVerwaltung')"
                  @change="addSupplementaryContracts()"
                >
                  <template
                    slot="selection"
                    slot-scope="data"
                  >
                    <div class="m-2 mutliselectSelectionItem">
                      {{ data.item.contractName }}
                      <button
                        @click.prevent="removeSupplementaryContract(data.item)"
                      >
                        <i class="fas fa-times" />
                      </button>
                    </div>
                  </template>

                  <template
                    slot="item"
                    slot-scope="data"
                  >
                    <div :id="data.item.contractNumber">
                      {{ data.item.contractNumber }} -
                      {{ data.item.contractName }}
                    </div>
                  </template>
                </v-autocomplete>
              </div>
            </b-row>
          </b-container>
        </div>

        <!-- Hauptverträge -->
        <div
          v-if="contract.contractTypeId === 5"
          class="form-group"
        >
          <b-container class="pb-0 pt-0">
            <b-row>
              <label
                :for="contract.value"
                class="pl-0 col-3 defaultLabel"
              >Hauptvertrag</label>
              <div class="col-9 pl-0">
                <div v-if="!editMode">
                  <ul v-if="mainContract">
                    <li @click="$router.go()">
                      <router-link
                        :to="
                          '/contract-overview/' + mainContract.contractNumber
                        "
                      >
                        {{ mainContract.contractNumber }} -
                        {{ mainContract.contractName }}
                      </router-link>
                    </li>
                  </ul>

                  <div v-else>
                    -
                  </div>
                </div>
                <v-autocomplete
                  v-else
                  v-model="mainContract"
                  :filter="filterContracts"
                  :items="allContracts"
                  item-value="id"
                  return-object
                  @change="addMainContracts"
                >
                  <template
                    slot="selection"
                    slot-scope="data"
                  >
                    <div class="m-2 mutliselectSelectionItem">
                      {{ data.item.contractName }}
                      <button @click.prevent="removeMainContract(data.item)">
                        <i class="fas fa-times" />
                      </button>
                    </div>
                  </template>

                  <template
                    slot="item"
                    slot-scope="data"
                  >
                    <div :id="data.item.id">
                      {{ data.item.contractNumber }} -
                      {{ data.item.contractName }}
                    </div>
                  </template>
                </v-autocomplete>
              </div>
            </b-row>
          </b-container>
        </div>

        <div class="form-group">
          <b-container class="pb-0 pt-0">
            <b-row>
              <label
                :for="contract.firstContractConclusion"
                class="pl-0 col-3 defaultLabel"
              >Erster Vertragsabschluss</label>
              <div
                v-if="!editMode"
                class="col-9 pl-0"
              >
                <span v-if="contract.firstContractConclusion">{{
                  contract.firstContractConclusion | moment("DD.MM.YYYY")
                }}</span>
                <span v-else>-</span>
              </div>
              <div
                v-else-if="editMode"
                class="col-9 pl-0"
              >
                <v-flex sm12>
                  <u-date-picker
                    v-model="contract.firstContractConclusion"
                    label="Vertragsabschluss"
                  />
                  <u-validation-checker
                    v-model="requiredProperties"
                    :is-empty="!contract.firstContractConclusion"
                    :section="validationSection"
                    property-name="firstContractConclusion"
                  />
                </v-flex>
              </div>
            </b-row>
          </b-container>
        </div>

        <div class="form-group">
          <b-container class="pb-0 pt-0">
            <b-row>
              <label
                :for="contract.contractResponsible"
                class="pl-0 col-2 defaultLabel"
              >Vertragsverantwortlicher</label>
              <div
                v-if="contractResponsiblesContainInactive"
                class="col-1"
              >
                <i
                  id="inactive-warning-icon"
                  class="fas fa-exclamation-triangle text-danger ml-2"
                />
                <b-tooltip target="inactive-warning-icon">
                  Es gibt Vertragsverantwortliche, die mit einem Kontakt mit
                  Status inaktiv verknüpft sind
                </b-tooltip>
              </div>
              <div class="col-9 pl-0">
                <u-loader v-if="isLoadingContractResponsiblity" />
                <span v-else>
                  <b-row
                    v-if="
                      editMode && $IsUserInRole('VertragsmanagementVerwaltung')
                    "
                    class="mb-4 pl-3"
                  >
                    <b-button
                      variant="success"
                      @click="addNewContractResponse"
                    >
                      <i class="fas fa-plus mr-2" />
                      Neuen Vertragsverantwortlichen erfassen
                    </b-button>
                  </b-row>

                  <b-row
                    v-if="
                      ContractResponsibles && ContractResponsibles.length > 1
                    "
                  >
                    <div class="col-12">
                      <v-flex sm12>
                        <v-btn
                          v-if="contactResponseLimit == 0"
                          class="float-right text-none"
                          color="blue-grey"
                          flat
                          style="margin-right: -1px"
                          @click="changeContactResponseLimit"
                        >
                          Alle Verantwortlichen anzeigen
                          <i class="fas fa-chevron-right ml-2" />
                        </v-btn>

                        <v-btn
                          v-else
                          class="float-right text-none"
                          color="blue-grey"
                          flat
                          style="margin-right: -1px"
                          @click="changeContactResponseLimit"
                        >
                          <span>Weniger Verantwortlichen anzeigen</span>
                          <i class="fas fa-chevron-down ml-2" />
                        </v-btn>
                      </v-flex>
                    </div>
                  </b-row>

                  <span
                    v-if="ContractResponsibles && ContractResponsibles.length"
                  >
                    <b-row
                      v-for="(
                        responsible, responsibleIndex
                      ) in ContractResponsibles"
                      :key="responsibleIndex"
                    >
                      <contract-responsible
                        v-if="responsibleIndex <= contactResponseLimit"
                        :contract-id="contractId"
                        :contract-responsible="responsible"
                        :edit-mode="
                          editMode &&
                            $IsUserInRole('VertragsmanagementVerwaltung')
                        "
                        :index="responsibleIndex"
                      />
                      <contract-responsible
                        v-else-if="
                          responsible.mainResponsible || responsible.id === 0
                        "
                        :contract-id="contractId"
                        :contract-responsible="responsible"
                        :edit-mode="
                          editMode &&
                            $IsUserInRole('VertragsmanagementVerwaltung')
                        "
                        :index="responsibleIndex"
                      />
                    </b-row>
                  </span>
                  <b-row
                    v-else
                    class="pl-3"
                  > - </b-row>
                </span>
              </div>
            </b-row>
          </b-container>
        </div>

        <div
          v-if="!editMode"
          class="form-group"
        >
          <b-container class="pb-0 pt-0">
            <b-row>
              <label class="pl-0 col-3 defaultLabel">Position in TOP GAV</label>
              <div class="col-9 pl-0">
                <div
                  v-if="contractPositionInTopGav == null"
                  class="float-left"
                >
                  keine
                </div>
                <div
                  v-else
                  class="float-left"
                >
                  {{ contractPositionInTopGav }}
                </div>

                <!-- not using router link here because we want to open a new Window to edit topgav -->
                <a
                  class="ml-1"
                  href="/topgav"
                >(ändern)</a>
              </div>
            </b-row>
          </b-container>
        </div>
        <div class="form-group">
          <div class="form-group">
            <b-container class="pb-0 pt-0">
              <b-row>
                <label
                  :for="contract.uniaInternal"
                  class="pl-0 col-3 defaultLabel"
                >Unia intern</label>
                <div class="form-check col-9 pl-0">
                  <label>
                    <input
                      v-model="contract.uniaInternal"
                      :disabled="
                        !editMode ||
                          !$IsUserInRole('VertragsmanagementVerwaltung')
                      "
                      name="check"
                      type="checkbox"
                    >
                    <span class="label-text" />
                  </label>
                  <u-validation-checker
                    v-if="editMode"
                    v-model="requiredProperties"
                    :is-empty="false"
                    :section="validationSection"
                    property-name="uniaInternal"
                  />
                </div>
              </b-row>
            </b-container>
          </div>
        </div>

        <div class="form-group">
          <b-container class="pb-0 pt-0">
            <b-row>
              <label
                class="pl-0 col-3 defaultLabel"
              >Anzahl unterstellter Betriebe</label>
              <div
                v-if="!editMode"
                class="col-9"
              >
                <b-row
                  v-if="
                    contract.companiesUnderContract == null ||
                      contract.companiesUnderContract.length == 0
                  "
                >
                  -
                </b-row>
                <b-row
                  v-for="(companies, index) in contract.companiesUnderContract"
                  v-else
                  :key="index"
                  class="mb-2"
                >
                  <b class="mr-2">{{ companies.year }}: </b>{{ companies.statsValue }}
                </b-row>
              </div>
              <div
                v-else
                class="col-9"
              >
                <b-row
                  v-for="(companies, index) in contract.companiesUnderContract"
                  :key="index"
                  align-v="center"
                >
                  <b-col>
                    <v-select
                      v-model="contract.companiesUnderContract[index].year"
                      :items="years"
                      label="Jahr"
                    />
                  </b-col>

                  <b-col>
                    <v-text-field
                      v-model="
                        contract.companiesUnderContract[index].statsValue
                      "
                      label="Firmen"
                      type="number"
                    />
                  </b-col>
                  <b-col>
                    <b-button
                      name="removeCompaniesUnderContractEntry"
                      variant="danger"
                      @click="removeCompaniesEntry(index)"
                    >
                      <i class="fas fa-times" />
                    </b-button>
                  </b-col>
                </b-row>
                <b-row>
                  <b-button
                    name="addCompaniesUnderContractEntry"
                    variant="success"
                    @click="addCompaniesEntry"
                  >
                    <i class="fas fa-plus" />
                  </b-button>
                </b-row>
                <b-row>
                  <u-validation-checker
                    v-model="requiredProperties"
                    :is-empty="contract.companiesUnderContract.length == 0"
                    :section="validationSection"
                    property-name="companiesUnderContract"
                  />
                </b-row>
              </div>
            </b-row>
          </b-container>
        </div>

        <div class="form-group">
          <b-container class="pb-0 pt-0">
            <b-row>
              <label
                class="pl-0 col-3 defaultLabel"
              >Anzahl unterstellter ArbeitnehmerInnen</label>
              <div
                v-if="!editMode"
                class="col-9"
              >
                <b-row
                  v-if="
                    contract.employeesUnderContract == null ||
                      contract.employeesUnderContract.length == 0
                  "
                >
                  -
                </b-row>
                <b-row
                  v-for="(employees, index) in contract.employeesUnderContract"
                  v-else
                  :key="index"
                  class="mb-2"
                >
                  <b class="mr-2">{{ employees.year }}: </b>{{ employees.statsValue }}
                </b-row>
              </div>
              <div
                v-else
                class="col-9"
              >
                <b-row
                  v-for="(employees, index) in contract.employeesUnderContract"
                  :key="index"
                  align-v="center"
                >
                  <b-col>
                    <v-select
                      v-model="contract.employeesUnderContract[index].year"
                      :items="years"
                      label="Jahr"
                    />
                  </b-col>

                  <b-col>
                    <v-text-field
                      v-model="
                        contract.employeesUnderContract[index].statsValue
                      "
                      label="ArbeitnehmerInnen"
                      type="number"
                    />
                  </b-col>
                  <b-col>
                    <b-button
                      name="removeEmployeesUnderContractEntry"
                      variant="danger"
                      @click="removeEmployeesEntry(index)"
                    >
                      <i class="fas fa-times" />
                    </b-button>
                  </b-col>
                </b-row>
                <b-row>
                  <b-button
                    name="addEmployeesUnderContractEntry"
                    variant="success"
                    @click="addEmployeesEntry"
                  >
                    <i class="fas fa-plus" />
                  </b-button>
                </b-row>
                <b-row>
                  <u-validation-checker
                    v-model="requiredProperties"
                    :is-empty="contract.employeesUnderContract.length == 0"
                    :section="validationSection"
                    property-name="employeesUnderContract"
                  />
                </b-row>
              </div>
            </b-row>
          </b-container>
        </div>

        <div class="form-group">
          <b-container class="pb-0 pt-0">
            <b-row>
              <label class="pl-0 col-3 defaultLabel">Kommentare</label>

              <div
                v-if="!editMode"
                class="col-9"
              >
                <div
                  v-if="
                    contract &&
                      contract.contractComments &&
                      contract.contractComments.length
                  "
                >
                  <b-row
                    v-if="
                      contract &&
                        contract.contractComments &&
                        contract.contractComments.length > 2
                    "
                  >
                    <div class="col-9">
                      <v-flex sm12>
                        <v-btn
                          v-if="commentLimit == 1"
                          class="float-right text-none"
                          color="blue-grey"
                          flat
                          style="margin-right: -14px"
                          @click="changeCommetLimit"
                        >
                          Alle Kommentare anzeigen
                          <i class="fas fa-chevron-right ml-2" />
                        </v-btn>

                        <v-btn
                          v-else
                          class="float-right text-none"
                          color="blue-grey"
                          flat
                          style="margin-right: -14px"
                          @click="changeCommetLimit"
                        >
                          Weniger Kommentare anzeigen
                          <i class="fas fa-chevron-down ml-2" />
                        </v-btn>
                      </v-flex>
                    </div>
                  </b-row>
                  <div
                    v-for="(comment, i) in contract.contractComments
                      .slice()
                      .reverse()"
                    :key="i"
                  >
                    <div v-if="i <= commentLimit">
                      <b-row>
                        <b-card
                          class="col-9"
                          mt-2
                          pt-4
                        >
                          <b-card-text>
                            <v-flex sm12>
                              <span><b>Gültig ab:</b>
                                {{
                                  comment.validFrom | moment("DD.MM.YYYY")
                                }}</span>
                            </v-flex>
                            <v-flex sm12>
                              <span><b>Gültig bis:</b>
                                {{
                                  comment.validTo | moment("DD.MM.YYYY")
                                }}</span>
                            </v-flex>
                            <v-flex sm12>
                              <span><b>Kommentar:</b>
                                {{ comment.commentText }}</span>
                            </v-flex>

                            <v-flex
                              pt-2
                              sm6
                            >
                              <label>
                                <input
                                  v-model="comment.finished"
                                  :disabled="!editMode"
                                  name="check"
                                  type="checkbox"
                                >
                                <span class="label-text" />
                                Erledigt
                              </label>
                            </v-flex>
                          </b-card-text>
                        </b-card>
                      </b-row>

                      <b-row style="opacity: 0">
                        --- Not Visible Row
                      </b-row>
                    </div>
                  </div>
                </div>
                <div v-else>
                  <b-row> - </b-row>
                </div>
              </div>
              <div
                v-else
                class="col-9"
              >
                <b-row>
                  <b-button
                    variant="success"
                    @click="addNewComment"
                  >
                    <i class="fas fa-plus mr-2" />
                    Neuen Kommentar erfassen
                  </b-button>
                </b-row>

                <div
                  v-if="
                    contract &&
                      contract.contractComments &&
                      contract.contractComments.length
                  "
                >
                  <div
                    v-for="(comment, i) in contract.contractComments
                      .slice()
                      .reverse()"
                    :key="i"
                  >
                    <b-row style="opacity: 0">
                      --- Not Visible Row
                    </b-row>

                    <b-row
                      v-if="
                        contract &&
                          contract.contractComments &&
                          contract.contractComments.length > 2 &&
                          i == 0
                      "
                    >
                      <div class="col-9">
                        <v-flex
                          class="pr-4"
                          sm12
                        >
                          <v-btn
                            v-if="commentLimit == 1"
                            class="float-right text-none"
                            color="blue-grey"
                            flat
                            style="margin-right: -36px"
                            @click="changeCommetLimit"
                          >
                            Alle Kommentare anzeigen
                            <i class="fas fa-chevron-right ml-2" />
                          </v-btn>
                          <v-btn
                            v-else
                            class="float-right text-none"
                            color="blue-grey"
                            flat
                            style="margin-right: -36px"
                            @click="changeCommetLimit"
                          >
                            Weniger Kommentare anzeigen
                            <i class="fas fa-chevron-down ml-2" />
                          </v-btn>
                        </v-flex>
                      </div>
                    </b-row>

                    <u-loader
                      v-if="isLoadingComments && i == 0"
                      class="col-9"
                      mt-2
                      pt-4
                    />

                    <b-row v-else-if="i <= commentLimit && !isLoadingComments">
                      <b-card
                        class="col-9"
                        mt-2
                        pt-4
                      >
                        <b-card-text>
                          <b-button
                            class="float-right"
                            variant="danger"
                            @click="deleteComment(i, comment)"
                          >
                            <i class="fas fa-times" />
                          </b-button>

                          <v-flex sm12>
                            <b-row style="opacity: 0">
                              --- Not Visible Row
                            </b-row>
                          </v-flex>

                          <v-flex
                            pt-4
                            sm12
                          >
                            <u-date-picker
                              v-model="comment.validFrom"
                              label="Kommentar gültig ab"
                            />
                          </v-flex>
                          <v-flex sm12>
                            <u-date-picker
                              v-model="comment.validTo"
                              label="Kommentar gültig bis"
                            />
                          </v-flex>

                          <v-text-field
                            v-model="comment.commentText"
                            class="mt-4"
                            label="Kommentar"
                          />

                          <v-flex
                            pt-2
                            sm6
                          >
                            <label>
                              <input
                                v-model="comment.finished"
                                name="check"
                                type="checkbox"
                              >
                              <span class="label-text" />
                              Erledigt
                            </label>
                          </v-flex>
                        </b-card-text>
                      </b-card>
                    </b-row>
                  </div>
                </div>
              </div>
            </b-row>
          </b-container>
        </div>

        <u-error-handling
          v-if="errorObject"
          :error-object="errorObject"
        />

        <div
          v-if="editMode"
          class="buttonContainer pt-4"
        >
          <b-button
            :disabled="requiredProperties.length != 0"
            variant="success"
            @click="saveContractDetails"
          >
            <i class="fas fa-save mr-3" /> Speichern
          </b-button>
          <b-button
            class="ml-2"
            variant="danger"
            @click="reloadContractDetails()"
          >
            <i class="fas fa-ban mr-3" /> Abbrechen
          </b-button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import {
  GetContractInformation,
  SaveContractInformation,
  GetPositionInTopGav,
  GetContractTypes,
  AddNewComment,
  DeleteComment,
  UpdateComment,
  GetContractTags,
  GetAllContractResponsiblity,
  SaveContractResponsiblity,
  GetAvailableSupplementaryContracts,
  GetSupplementaryContracts,
  PostSupplementaryContracts,
  DeleteSupplementaryContracts,
  GetAllContractsNoVersions,
  GetMainContracts,
} from "@/services/contract.service.js";
import { GetContactsWithId } from "@/services/user.crm.service";
import UDatePicker from "@/components/common/UDatePicker.vue";
import { range } from "@/helpers/RangeHelper.js";

import ContractResponsible from "@/components/contract/ContractResponsible.vue";
import contractResponsible from "@/components/contract/ContractResponsible.vue";

export default {
  components: {
    UDatePicker,
    ContractResponsible,
  },
  props: {
    contractId: {
      type: String,
      default: "",
    },
    latestVersionShortName: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      validationSection: "Vertrag",
      contract: null,
      editMode: false,
      isLoading: false,
      isLoadingComments: false,
      isLoadingContractResponsiblity: false,
      errorObject: null,
      contractPositionInTopGav: -1,
      contractContentIds: [
        { language: "de-CH", value: -1 },
        { language: "fr-CH", value: -1 },
        { language: "it-CH", value: -1 },
      ],
      contractLanguages: [
        { language: "de-CH", label: "Deutsch", selected: false },
        { language: "fr-CH", label: "Französisch", selected: false },
        { language: "it-CH", label: "Italienisch", selected: false },
      ],
      contractTypes: [],
      years: [],
      requiredProperties: [],
      newComment: {
        id: 0,
        contractId: 0,
        validFrom: null,
        validTo: null,
        finished: false,
        commentText: "",
      },
      itemsTags: [],
      ContractResponsibles: [],
      commentLimit: 1,
      contactResponseLimit: 0,
      newContractResponsibles: [],
      availableSupplementaryContracts: [],
      supplementaryContracts: [],
      originalSupplementaryContracts: [],
      allContracts: [],
      mainContract: null,
      originalMainContract: null,
      addedSupplementaryContracts: [],
      removedSupplementaryContracts: [],
      addedMainContracts: [],
      removedMainContracts: [],
    };
  },
  computed: {
    contractResponsible() {
      return contractResponsible;
    },
    contractType() {
      for (var contractType of this.contractTypes) {
        if (contractType.id == this.contract.contractTypeId) {
          return contractType.name;
        }
      }

      return "-";
    },
    contactsOfResponsibles() {
      let contacts = [];
      this.ContractResponsibles.forEach((responsible) => {
        if (responsible.contactId && responsible.contactId > 0) {
          GetContactsWithId(
            responsible.contactId,
            (response) => {
              if (response) {
                contacts.push(response);
              }
            },
            () => {},
          );
        }
      });

      return contacts;
    },
    contractResponsiblesContainInactive() {
      return this.contactsOfResponsibles.some((contact) => contact.inactive);
    },
  },

  created: function () {
    this.years = range(1990, 2100);
    this.getContractTags();
    this.loadContractDetails();
  },
  methods: {
    addMainContracts() {
      if (!this.mainContract && this.originalMainContract) {
        this.addedMainContracts = [];
        this.removedMainContracts = [this.originalMainContract];
      } else if (this.mainContract && !this.originalMainContract) {
        this.addedMainContracts = [this.mainContract];
        this.removedMainContracts = [];
      } else if (
        this.mainContract.contractNumber !==
        this.originalMainContract.contractNumber
      ) {
        this.addedMainContracts = [this.mainContract];
        this.removedMainContracts = [this.originalMainContract];
      }
    },
    getMainContracts() {
      this.mainContract = null;
      GetMainContracts(
        this.contractId,
        (response) => {
          if (response && response.length) {
            let counter = 0;
            response.forEach((mainContractNumber) => {
              const foundContract = this.allContracts.find(
                (contract) => contract.contractNumber === mainContractNumber,
              );
              if (foundContract) {
                this.mainContract = foundContract;
              }
              counter++;
            });
            if (response.length === counter) {
              this.originalMainContract = JSON.parse(
                JSON.stringify(this.mainContract),
              );
            }
          }
        },
        (error) => {
          this.errorObject = error;
        },
      );
    },
    getAllContracts() {
      GetAllContractsNoVersions(
        (response) => {
          if (response.data && response.data.length) {
            response.data.forEach((contract) => {
              if (contract.contractNumber !== this.contract.contractNumber) {
                this.allContracts.push({
                  id: contract.id,
                  contractNumber: contract.contractNumber,
                  contractName: contract.contractName,
                });
              }
            });

            this.getMainContracts();
          }
        },
        (error) => {
          this.errorObject = error;
        },
      );
    },
    getContractName(contractNumber) {
      var contract = this.availableSupplementaryContracts.find((element) => {
        return element.contractNumber == contractNumber;
      });

      if (contract == null) {
        return "-";
      }

      return contract.contractName;
    },
    removeSupplementaryContract(item) {
      this.supplementaryContracts = this.supplementaryContracts.filter(
        (filterItem) => {
          return filterItem.contractNumber != item.contractNumber;
        },
      );
      this.removedSupplementaryContracts.push(item);
    },
    removeMainContract(item) {
      this.removedMainContracts.push(item);

      this.mainContract = null;
    },
    addSupplementaryContracts() {
      // Elemente in supplementaryContracts, die nicht in originalSupplementaryContracts enthalten sind (hinzugefügte Elemente)
      this.addedSupplementaryContracts = this.supplementaryContracts.filter(
        (item1) =>
          !this.originalSupplementaryContracts.find(
            (item2) => item1.id === item2.id,
          ),
      );
      // Elemente in originalSupplementaryContracts, die nicht in supplementaryContracts enthalten sind (entfernte Elemente)
      this.removedSupplementaryContracts =
        this.originalSupplementaryContracts.filter(
          (item2) =>
            !this.supplementaryContracts.find((item1) => item2.id === item1.id),
        );
    },
    async postSupplementaryContracts(
      mainContractNumber,
      supplementaryContractNumber,
    ) {
      try {
        await PostSupplementaryContracts(
          mainContractNumber,
          supplementaryContractNumber,
          (repsonse) => {
            return repsonse;
          },
          (error) => {
            this.errorObject = error;
            return error;
          },
        );
      } catch (error) {
        this.errorObject = error;
        return error;
      }
    },
    async removeMainContracts(removeContractNumber) {
      try {
        await DeleteSupplementaryContracts(
          removeContractNumber,
          this.contract.contractNumber,
          (repsonse) => {
            return repsonse;
          },
          (error) => {
            this.errorObject = error;
            return error;
          },
        );
      } catch (error) {
        this.errorObject = error;
        return error;
      }
    },
    async removeSupplementaryContracts(removeContractNumber) {
      try {
        await DeleteSupplementaryContracts(
          this.contract.contractNumber,
          removeContractNumber,
          (repsonse) => {
            return repsonse;
          },
          (error) => {
            this.errorObject = error;
            return error;
          },
        );
      } catch (error) {
        this.errorObject = error;
        return error;
      }
    },
    getSupplementaryContracts() {
      GetAvailableSupplementaryContracts(
        (response) => {
          if (response && response.length) {
            this.availableSupplementaryContracts = response.filter(
              (item) => item.contractNumber !== this.contract.contractNumber,
            );

            GetSupplementaryContracts(
              this.contract.contractNumber,
              (response2) => {
                if (response2 && response2.length) {
                  let counter = 0;
                  response2.forEach((response2_ContractNumber) => {
                    const foundContract = response.find(
                      (response_ContractNumber) => {
                        if ("contractNumber" in response_ContractNumber) {
                          return (
                            response_ContractNumber.contractNumber ===
                            response2_ContractNumber
                          );
                        }
                      },
                    );

                    if (foundContract) {
                      this.supplementaryContracts.push(foundContract);
                    }

                    counter++;
                  });
                  if (response2.length === counter) {
                    this.originalSupplementaryContracts = JSON.parse(
                      JSON.stringify(this.supplementaryContracts),
                    );
                  }
                }
              },
              (error2) => {
                this.errorObject = error2;
              },
            );
          }
        },
        (error) => {
          this.errorObject = error;
        },
      );
    },
    filterContracts: (option, search) => {
      let temp = search.toLowerCase();
      return (
        option.contractNumber.toLowerCase().indexOf(temp) > -1 ||
        option.contractName.toLowerCase().indexOf(temp) > -1
      );
    },
    reloadContractResponse(index) {
      this.changeContactResponseLimit();
      if (this.ContractResponsibles[index].id === 0) {
        this.removeContractResponse(index);
      } else {
        this.getNewContractresponsibles();
      }
      window.scrollTo(0, 0);
    },
    removeContractResponse(index) {
      this.ContractResponsibles.splice(index, 1);
      this.getNewContractresponsibles();
    },
    getNewContractresponsibles() {
      const newResponsibles = this.ContractResponsibles.filter(
        (responsible) => responsible.id === 0,
      );
      if (newResponsibles) {
        this.newContractResponsibles = newResponsibles;
      }
      this.getContractresponsibles();
    },
    getContractresponsibles() {
      this.isLoadingContractResponsiblity = true;
      GetAllContractResponsiblity(
        this.contractId,
        (response) => {
          this.ContractResponsibles = response;

          //Show MainResponsible first
          this.ContractResponsibles.sort(
            (a, b) => b.mainResponsible - a.mainResponsible,
          );

          if (
            this.newContractResponsibles &&
            this.newContractResponsibles.length
          ) {
            this.ContractResponsibles.unshift(...this.newContractResponsibles);
          }

          this.isLoadingContractResponsiblity = false;
        },
        (error) => {
          this.errorObject = error;
          this.isLoadingContractResponsiblity = false;
        },
      );
    },
    addNewContractResponse() {
      this.ContractResponsibles.unshift({
        id: 0,
        contractId: this.contract.id,
        organisationId: null,
        contactId: 0,
        mainResponsible: false,
        types: "",
        typeList: [],
      });
    },
    changeContactResponseLimit() {
      if (this.contactResponseLimit === 0) {
        this.contactResponseLimit = this.ContractResponsibles.length;
      } else {
        this.contactResponseLimit = 0;
      }
    },
    changeCommetLimit() {
      if (this.commentLimit == 1) {
        this.commentLimit = this.contract.contractComments.length;
      } else {
        this.commentLimit = 1;
      }
    },
    tagsChanged() {
      if (
        this.contract &&
        this.contract.tagList &&
        this.contract.tagList.length > 1
      ) {
        this.contract.tagList.forEach((value) => {
          if (this.itemsTags.indexOf(value) === -1) {
            this.itemsTags.push(value);
          }
        });
      } else {
        if (this.contract.tagList[0]) {
          this.itemsTags.push(this.contract.tagList[0]);
        }
      }
    },
    getContractTags() {
      GetContractTags(
        (response) => {
          if (response.data.length) {
            response.data.forEach((tag) => {
              this.itemsTags.push(tag.tagName);
            });
          }
        },
        (error) => {
          this.errorObject = error;
        },
      );
    },
    addNewComment() {
      if (this.contract && this.contract.contractComments) {
        this.isLoadingComments = true;
        this.newComment.contractId = this.contract.id;

        AddNewComment(
          this.newComment,
          this.contractId,
          (response) => {
            this.contract.contractComments.push(
              JSON.parse(JSON.stringify(response)),
            );
            this.resetComments();
            this.isLoadingComments = false;
          },
          (error) => {
            this.errorObject = error;
            this.isLoadingComments = false;
          },
        );
      }
    },
    resetComments() {
      this.newComment.commentText = "";
      this.newComment.validFrom = "";
      this.newComment.validTo = "";
    },
    deleteComment(index, comment) {
      this.isLoadingComments = true;

      // tempContractComments.slice().reverse();
      if (comment.id == 0) {
        this.contract.contractComments.splice(index, 1);
        this.isLoadingComments = false;
      } else {
        DeleteComment(
          this.contractId,
          comment.id,
          () => {
            this.contract.contractComments =
              this.contract.contractComments.filter(
                (contractComment) => contractComment.id != comment.id,
              );

            this.isLoadingComments = false;
          },
          (error) => {
            this.errorObject = error;
            this.isLoadingComments = false;
          },
        );
      }
    },
    addCompaniesEntry() {
      var numberOfEntries = this.contract.companiesUnderContract.length;
      var currentYear = 0;

      if (numberOfEntries == 0) {
        currentYear = parseInt(this.$moment().format("YYYY"));
      } else {
        currentYear =
          this.contract.companiesUnderContract[numberOfEntries - 1].year + 1;
      }

      this.contract.companiesUnderContract.push({
        year: currentYear,
        statsValue: 0,
      });
    },
    addEmployeesEntry() {
      var numberOfEntries = this.contract.employeesUnderContract.length;
      var currentYear = 0;

      if (numberOfEntries == 0) {
        currentYear = parseInt(this.$moment().format("YYYY"));
      } else {
        currentYear =
          this.contract.employeesUnderContract[numberOfEntries - 1].year + 1;
      }

      this.contract.employeesUnderContract.push({
        year: currentYear,
        statsValue: 0,
      });
    },
    removeCompaniesEntry(index) {
      this.contract.companiesUnderContract.splice(index, 1);
    },
    removeEmployeesEntry(index) {
      this.contract.employeesUnderContract.splice(index, 1);
    },
    saveContractDetails() {
      let that = this;
      this.isLoading = true;
      this.errorObject = null;
      if (this.ContractResponsibles && this.ContractResponsibles.length) {
        this.ContractResponsibles.forEach((contractResponsible, index) => {
          SaveContractResponsiblity(
            this.contractId,
            contractResponsible,
            () => {
              this.reloadContractResponse(index);
            },
            (error) => {
              this.errorObject = error;
            },
          );
        });
      }
      if (this.contract.contractComments.length) {
        this.contract.contractComments.forEach((comment) => {
          UpdateComment(
            comment,
            this.contractId,
            () => {
              window.scrollTo(0, 0);
            },
            (error) => {
              this.errorObject = error;
            },
          );
        });
      }

      SaveContractInformation(
        this.contract,
        this.contractId,
        () => {
          that.editMode = false;

          if (
            this.addedMainContracts.length ||
            this.addedSupplementaryContracts.length ||
            this.removedSupplementaryContracts.length ||
            this.removedMainContracts.length
          ) {
            let operationCount = 0;

            if (this.addedMainContracts.length) {
              this.addedMainContracts.forEach((mainContract) => {
                this.postSupplementaryContracts(
                  mainContract.contractNumber,
                  this.contract.contractNumber,
                ).then(() => {
                  operationCount++;
                  this.checkAndSetLoadingStatus(operationCount);
                });
              });
            }

            if (this.addedSupplementaryContracts.length) {
              this.addedSupplementaryContracts.forEach(
                (supplementaryContract) => {
                  this.postSupplementaryContracts(
                    this.contract.contractNumber,
                    supplementaryContract.contractNumber,
                  ).then(() => {
                    operationCount++;
                    this.checkAndSetLoadingStatus(operationCount);
                  });
                },
              );
            }

            if (this.removedSupplementaryContracts.length) {
              this.removedSupplementaryContracts.forEach(
                (supplementaryContract) => {
                  this.removeSupplementaryContracts(
                    supplementaryContract.contractNumber,
                  ).then(() => {
                    operationCount++;
                    this.checkAndSetLoadingStatus(operationCount);
                  });
                },
              );
            }

            if (this.removedMainContracts.length) {
              this.removedMainContracts.forEach((mainContract) => {
                this.removeMainContracts(mainContract.contractNumber).then(
                  () => {
                    operationCount++;
                    this.checkAndSetLoadingStatus(operationCount);
                  },
                );
              });
            }
          } else {
            this.isLoading = false;
          }

          if (this.contract.companiesUnderContract) {
            this.contract.companiesUnderContract.sort(
              (a, b) => b.year - a.year,
            );
          }
          if (this.contract.employeesUnderContract) {
            this.contract.employeesUnderContract.sort(
              (a, b) => b.year - a.year,
            );
          }
        },
        (error) => {
          that.errorObject = error;
          that.isLoading = false;
        },
      );
    },
    checkAndSetLoadingStatus(operationCount) {
      if (operationCount === this.getTotalOperationCount()) {
        this.originalSupplementaryContracts = JSON.parse(
          JSON.stringify(this.supplementaryContracts),
        );
        this.originalMainContract = JSON.parse(
          JSON.stringify(this.mainContract),
        );

        this.addedMainContracts = [];
        this.addedSupplementaryContracts = [];
        this.removedSupplementaryContracts = [];
        this.removedMainContracts = [];

        this.isLoading = false;
      }
    },
    getTotalOperationCount() {
      return (
        this.addedMainContracts.length +
        this.addedSupplementaryContracts.length +
        this.removedSupplementaryContracts.length +
        this.removedMainContracts.length
      );
    },

    reloadContractDetails() {
      this.supplementaryContracts = JSON.parse(
        JSON.stringify(this.originalSupplementaryContracts),
      );
      this.mainContract = JSON.parse(JSON.stringify(this.originalMainContract));
      this.loadContractDetails();
      this.editMode = false;
    },

    loadContractDetails() {
      this.isLoading = true;
      this.errorObject = null;
      GetContractInformation(
        this.contractId,
        (result) => {
          if (result.contractLanguage) {
            this.$store.commit(
              "setAdminCurrentContractContractLanguage",
              result.contractLanguage,
            );
          }

          this.contract = result;

          this.getAllContracts();
          this.getContractresponsibles();

          if (this.contract.companiesUnderContract) {
            this.contract.companiesUnderContract.sort(
              (a, b) => b.year - a.year,
            );
          }
          if (this.contract.employeesUnderContract) {
            this.contract.employeesUnderContract.sort(
              (a, b) => b.year - a.year,
            );
          }

          this.$store.commit(
            "setAdminCurrentContractContractNumber",
            this.contract.contractNumber,
          );

          let that = this;

          if (
            this.supplementaryContracts &&
            !this.supplementaryContracts.length
          ) {
            this.getSupplementaryContracts();
          }

          GetPositionInTopGav(
            that.contractId,
            (response) => {
              that.contractPositionInTopGav = response;

              GetContractTypes(
                (response) => {
                  that.contractTypes = response.data;
                  that.isLoading = false;
                },
                (error) => {
                  that.errorObject = error;
                  that.isLoading = false;
                },
              );
            },
            (error) => {
              that.errorObject = error;
              that.isLoading = false;
            },
          );
        },
        (error) => {
          this.isLoading = false;
          this.errorObject = error;
        },
      );
    },
  },
};
</script>

<style>
.theme--light.v-messages {
  display: none;
}
</style>

<style scoped>
.card-title {
  margin-bottom: 40px;
}

.form-group {
  font-size: initial;
}
</style>
